import React, { useState, useEffect } from 'react'
import { Modal, Form, Message } from 'semantic-ui-react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { X } from 'phosphor-react'
import { FlexRowBetween, TitleText, FileUploadPicker } from 'components'
import {
    FormTextFormField,
    FormDropdownField,
    FormTextAreaFormField,
    SubmitCancelButtons,
} from 'elements'
import { SupportTicketService } from 'utility/services/support-ticket'
import { InspectionService } from 'utility/services/inspection'
import { useAuth } from 'context/user'

const contactUsSchema = yup.object().shape({
    Name: yup.string().required('Name is required'),
    Email: yup.string().email('Invalid email address').required('Email is required'),
    PhoneNumber: yup.string().nullable(),
    IssueTypeId: yup.number().required('Issue type is required'),
    Message: yup
        .string()
        .required('Message is required')
        .min(10, 'Message must be at least 10 characters'),
    Attachments: yup.array().nullable(),
})

export const ContactUsModal = ({ open, onClose }) => {
    const { user } = useAuth()
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const [attachments, setAttachments] = useState([])
    const [attachmentError, setAttachmentError] = useState('')
    const [issueTypes, setIssueTypes] = useState([])
    const [loadingIssueTypes, setLoadingIssueTypes] = useState(false)

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
        setValue,
    } = useForm({
        resolver: yupResolver(contactUsSchema),
        defaultValues: {
            Name: '',
            Email: '',
            PhoneNumber: '',
            IssueTypeId: null,
            Message: '',
            Attachments: [],
        },
    })

    // Fetch issue types when modal opens
    useEffect(() => {
        if (open) {
            fetchIssueTypes()
            if (user) {
                setValue('Name', user.FullName || '')
                setValue('Email', user.Email || '')
            }
        }
    }, [open, user, setValue])

    const fetchIssueTypes = async () => {
        try {
            setLoadingIssueTypes(true)
            const types = await SupportTicketService.getIssueTypes()
            setIssueTypes(Array.isArray(types) ? types : [])
        } catch (error) {
            console.error('Error fetching issue types:', error)
            setIssueTypes([])
        } finally {
            setLoadingIssueTypes(false)
        }
    }

    // Reset form when modal closes
    useEffect(() => {
        if (!open) {
            reset()
            setAttachments([])
            setSuccess(false)
            setAttachmentError('')
        }
    }, [open, reset])

    // Validate attachment size
    const validateAttachmentSize = (files) => {
        const maxSize = 10 * 1024 * 1024 // 10MB in bytes
        const invalidFiles = files.filter((file) => {
            // FileUploadPicker returns files in format: { file: { file: actualFile }, ... }
            const actualFile = file.file?.file || file.file || file
            const fileSize = actualFile.size || 0
            return fileSize > maxSize
        })

        if (invalidFiles.length > 0) {
            setAttachmentError('Some files exceed the 10MB size limit')
            return false
        }
        setAttachmentError('')
        return true
    }

    const handleFileUpload = (files) => {
        if (!validateAttachmentSize(files)) {
            return
        }

        // Extract actual file objects from FileUploadPicker format
        const newFiles = files.map((file) => {
            // FileUploadPicker format: { file: { file: actualFile }, url, id, ... }
            return file.file?.file || file.file || file
        })

        setAttachments(newFiles)
        setValue('Attachments', newFiles)
    }

    const handleFileRemove = (fileToRemove) => {
        // Find the index of the file to remove
        const fileToRemoveActual = fileToRemove.file?.file || fileToRemove.file || fileToRemove
        const updatedFiles = attachments.filter((file) => {
            const fileActual = file.file?.file || file.file || file
            return fileActual !== fileToRemoveActual
        })
        setAttachments(updatedFiles)
        setValue('Attachments', updatedFiles)
    }

    const onSubmit = async (data) => {
        try {
            setLoading(true)
            setSuccess(false)

            // Upload attachments first if any
            let attachmentUrls = []
            if (attachments && attachments.length > 0) {
                try {
                    const uploadPromises = attachments.map(async (file) => {
                        const actualFile = file.file?.file || file.file || file
                        if (actualFile instanceof File) {
                            // Upload file to S3
                            const uploadResponse = await InspectionService.uploadMedia({
                                media: actualFile,
                            })

                            // Save media record and get the URL
                            const savedMedia = await InspectionService.saveMedia([
                                {
                                    MediaId: 0,
                                    FAQId: '',
                                    Order: 1,
                                    FolderName: '1',
                                    FileType: actualFile.type,
                                    SignedURL: uploadResponse.ImagePathUrl || uploadResponse.FileURL,
                                    FileURL: uploadResponse.ImagePathUrl || uploadResponse.FileURL,
                                    FileKey: uploadResponse.CloudImageId || uploadResponse.FileKey,
                                },
                            ])

                            return {
                                AttachmentId: 0, // 0 for new attachments
                                TicketId: 0, // 0 since ticket doesn't exist yet
                                FilePath: savedMedia[0]?.FileURL || savedMedia[0]?.SignedURL || uploadResponse.ImagePathUrl || uploadResponse.FileURL,
                                CreatedOn: new Date().toISOString(), // Current timestamp
                            }
                        }
                        // If already a URL, return as is
                        return {
                            AttachmentId: 0,
                            TicketId: 0,
                            FilePath: file.url || file.FilePath || file,
                            CreatedOn: new Date().toISOString(),
                        }
                    })

                    attachmentUrls = await Promise.all(uploadPromises)
                } catch (uploadError) {
                    console.error('Error uploading attachments:', uploadError)
                    throw new Error('Failed to upload attachments. Please try again.')
                }
            }

            // Prepare ticket data
            const ticketData = {
                TicketId: 0,
                RequesterName: data.Name,
                RequesterEmail: data.Email,
                PhoneNumber: data.PhoneNumber || null,
                IssueTypeId: data.IssueTypeId,
                Message: data.Message,
                StatusId: 1, // Open
                CreatedByUserId: user?.unique_id || user?.Id || null,
                Attachments: attachmentUrls,
            }

            // Submit to API
            await SupportTicketService.create(ticketData)

            // Show success message
            setSuccess(true)

            // Reset form after 2 seconds and close modal
            setTimeout(() => {
                reset()
                setAttachments([])
                setSuccess(false)
                onClose()
            }, 2000)
        } catch (error) {
            console.error('Error submitting support ticket:', error)
            setAttachmentError(error.message || 'Failed to submit ticket. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal open={open} onCancel={onClose} className="model" size="small">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>Raise a Support Ticket</TitleText>
                    <X className="pointer" size={32} onClick={onClose} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                {success && (
                    <Message success>
                        <Message.Header>Thank you!</Message.Header>
                        <p>Our team will respond shortly.</p>
                    </Message>
                )}
                <Form noValidate>
                    <Form.Field>
                        <FormTextFormField
                            control={control}
                            label="Name"
                            name="Name"
                            placeholder="Enter your name"
                            errors={errors?.Name}
                            defaultValue=""
                            required
                        />
                    </Form.Field>
                    <Form.Field>
                        <FormTextFormField
                            control={control}
                            label="Email"
                            name="Email"
                            placeholder="Enter your email"
                            errors={errors?.Email}
                            defaultValue=""
                            required
                            disabled
                        />
                        <small style={{ color: '#666', fontSize: '0.875rem' }}>
                            Email is auto-populated from your account
                        </small>
                    </Form.Field>
                    <Form.Field>
                        <FormTextFormField
                            control={control}
                            label="Phone Number"
                            name="PhoneNumber"
                            placeholder="Enter your phone number (optional)"
                            errors={errors?.PhoneNumber}
                            defaultValue=""
                            type="tel"
                            onKeyPress={(e) => {
                                // Prevent alphabetic characters from being entered
                                const char = String.fromCharCode(e.which)
                                if (!/[0-9+\-() ]/.test(char)) {
                                    e.preventDefault()
                                }
                            }}
                            onChange={(value) => {
                                // Filter out any alphabetic characters that might have been pasted
                                const filteredValue = value.replace(/[^0-9+\-() ]/g, '')
                                if (filteredValue !== value) {
                                    setValue('PhoneNumber', filteredValue, { shouldValidate: true })
                                }
                            }}
                        />
                    </Form.Field>
                    <Form.Field>
                        <FormDropdownField
                            control={control}
                            label="Issue Type"
                            name="IssueTypeId"
                            placeholder={loadingIssueTypes ? "Loading issue types..." : "Select issue type"}
                            errors={errors?.IssueTypeId}
                            options={issueTypes.map((type) => ({
                                key: type.IssueTypeId || type.Id,
                                text: type.IssueTypeName || type.Name,
                                value: type.IssueTypeId || type.Id,
                            }))}
                            defaultValue={null}
                            required
                            disabled={loadingIssueTypes}
                        />
                    </Form.Field>
                    <Form.Field>
                        <FormTextAreaFormField
                            control={control}
                            label="Message"
                            name="Message"
                            placeholder="Enter your message (minimum 10 characters)"
                            errors={errors?.Message}
                            defaultValue=""
                            required
                            height="120px"
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Attachments (Optional)</label>
                        <FileUploadPicker
                            maxFiles={5}
                            maxFileSize={10}
                            accept={{
                                'image/*': ['.jpeg', '.png', '.jpg', '.gif'],
                                'application/pdf': ['.pdf'],
                            }}
                            addedNewFiles={handleFileUpload}
                            files={attachments.map((file, index) => {
                                const fileObj = file.file?.file || file.file || file
                                return {
                                    id: file.id || `file-${index}`,
                                    file: {
                                        file: fileObj,
                                        name: fileObj.name || `File ${index + 1}`,
                                    },
                                    name: fileObj.name || `File ${index + 1}`,
                                    url:
                                        file.url ||
                                        (fileObj instanceof File
                                            ? URL.createObjectURL(fileObj)
                                            : fileObj.url),
                                    local: true,
                                }
                            })}
                            onRemove={handleFileRemove}
                            remove
                        />
                        {attachmentError && (
                            <p
                                style={{
                                    color: 'red',
                                    marginTop: '4px',
                                    fontSize: '0.875rem',
                                }}
                            >
                                {attachmentError}
                            </p>
                        )}
                        <small style={{ color: '#666', fontSize: '0.875rem' }}>
                            Maximum file size: 10MB. Supported formats: Images, PDFs
                        </small>
                    </Form.Field>
                    <SubmitCancelButtons
                        loading={loading}
                        onSubmit={handleSubmit(onSubmit)}
                        onCancel={onClose}
                    />
                </Form>
            </Modal.Content>
        </Modal>
    )
}

export default ContactUsModal
